import { Badge, FlagFilledSVG } from "@newsela/angelou";
import { useRouter } from "next/router";

import dayjs from "dayjs";
import VISIBILITY_MARKER_REASONS from "@/constants/VISIBILITY_MARKER_REASONS";

import useGetUserProperties from "@/users/v3/useGetUserProperties";
import { OWN_ORIGIN } from "@/base/base.constants";
import { getFormattedLexileLevelLabel } from "@/components/ContentCardV2/ContentCardV2.utils";
import {
  getAssignHref,
  getFormattedGradeLevelLabel,
  durationFormatted,
} from "../ContentCardV2.utils";

import { ContentTypeEnum } from "../ContentCardV2.interface";

import {
  CONTENT_CARD_READ_UNTIL_DATE_TIME_FORMAT,
  ENABLE_ASSIGNMENT_ACTIONS_TEXT_SET_TYPES,
} from "../ContentCardV2.constants";

interface UseContentCardProps {
  contentId: string;
  contentType: string;
  dateLiteProductGrantExpires?: string | null;
  durationInSeconds?: number;
  label?: string;
  maturityRange?: number[];
  lexileLevels?: number[];
  visibilityMarkers?:
    | {
        reason: string;
        flagged_at_my_admin_level: boolean;
      }[]
    | undefined;
}

export const useContentCard = ({
  contentId,
  contentType,
  dateLiteProductGrantExpires,
  durationInSeconds,
  label = "",
  maturityRange,
  lexileLevels,
  visibilityMarkers,
}: UseContentCardProps) => {
  const { asPath } = useRouter();
  const { isLiteUser, isParent, isStudent, isTeacher } = useGetUserProperties();

  const formattedMaturityGradeLevelLabel =
    getFormattedGradeLevelLabel(maturityRange);

  const formattedReadingLevelLabel = getFormattedLexileLevelLabel(lexileLevels);

  const durationMetadata = durationInSeconds
    ? durationFormatted(durationInSeconds)
    : null;

  const currentDate = dayjs();
  const dateLiteProductGrantExpiresFormatted =
    !!dateLiteProductGrantExpires && dayjs(dateLiteProductGrantExpires);
  const contentHasExpired = currentDate > dateLiteProductGrantExpiresFormatted;

  const isFreeContent =
    isLiteUser && dateLiteProductGrantExpires && !contentHasExpired;
  const isFreeArticle = isLiteUser && !!dateLiteProductGrantExpires;
  const showPremiumTag =
    (isLiteUser && (!dateLiteProductGrantExpires || contentHasExpired)) ||
    (isTeacher &&
      (!dateLiteProductGrantExpires || contentHasExpired) &&
      visibilityMarkers?.find(
        (marker) => marker.reason === VISIBILITY_MARKER_REASONS.PREMIUM_CONTENT,
      ));
  const showAdvisoryTag =
    isTeacher &&
    visibilityMarkers?.find(
      (marker) =>
        marker.reason === VISIBILITY_MARKER_REASONS.DISTRICT_ADVISORY ||
        marker.reason === VISIBILITY_MARKER_REASONS.SCHOOL_ADVISORY,
    );
  const showFlaggedTag =
    !isTeacher &&
    visibilityMarkers?.find((marker) => !!marker?.flagged_at_my_admin_level);

  const showAssignmentAnchor =
    !isStudent &&
    !isParent &&
    (!isLiteUser || isFreeArticle) &&
    // From the database, every Bundle with non-empty "data" field (a data
    // error) has a "label" value. Smart Bundles don't have labels, and they
    // should not be assignable.
    (contentType !== ContentTypeEnum.BUNDLE ||
      ENABLE_ASSIGNMENT_ACTIONS_TEXT_SET_TYPES.has(label.toLowerCase())) &&
    !visibilityMarkers?.find(
      (marker) => marker.reason === VISIBILITY_MARKER_REASONS.PREMIUM_CONTENT,
    );

  const returnUrl = `${OWN_ORIGIN}${asPath}`;
  const createAssignmentUrl = `/assignments/create?return_url=${returnUrl}&createdFrom=${contentId}&contentIds=${contentId}`;
  const assignHref = getAssignHref(returnUrl, contentId, contentType);

  const badges = [
    ...(isFreeContent
      ? [
          {
            text: `Read Until ${dayjs(dateLiteProductGrantExpires).format(CONTENT_CARD_READ_UNTIL_DATE_TIME_FORMAT)}`,
            color: Badge.color.blue,
          },
        ]
      : []),
    ...(showPremiumTag
      ? [
          {
            text: "Premium",
            color: Badge.color.blue,
          },
        ]
      : []),
    ...(showAdvisoryTag
      ? [
          {
            text: "Advisory",
            color: Badge.color.yellow,
          },
        ]
      : []),
    ...(showFlaggedTag
      ? [
          {
            text: "Flagged",
            SvgIcon: FlagFilledSVG,
            color: Badge.color.yellow,
          },
        ]
      : []),
  ];

  return {
    assignHref,
    badges,
    createAssignmentUrl,
    durationMetadata,
    formattedMaturityGradeLevelLabel,
    formattedReadingLevelLabel,
    showAssignmentAnchor,
  };
};
